import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import dashboardApi from "../components/dashboardApi";
import { calculateMetrics } from "../utils/emotionCalculations";
import "./SchoolUniversityHome.css";
import useTranslation from "../hooks/useTranslation";
import useTheme from "../hooks/useTheme";
import { EMOTION_KEYS } from "../utils/emotionConstants";
import { authFetch, authHeaders } from '../utils/authFetch';
import { compressImage } from '../utils/imageCompression';
import StudentListPanel from "../components/school/StudentListPanel";
import SessionRecordingPanel from "../components/school/SessionRecordingPanel";
import ReportModal from "../components/school/ReportModal";
import useStudentImportExport from "../components/school/useStudentImportExport";
import useStaffImportExport from "../components/school/useStaffImportExport";

const DEFAULT_AVATAR = "/profile.jpg";
const isBrowser = typeof window !== "undefined";

const normalizeBaseUrl = (url) => {
  if (!url) return "";
  return String(url).trim().replace(/\/+$/, "");
};

const resolveBackendOrigin = () => {
  const envBase = normalizeBaseUrl(process.env.REACT_APP_API_URL);
  if (envBase) {
    return envBase.endsWith("/api") ? envBase.slice(0, -4) : envBase;
  }
  if (isBrowser) {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return "http://localhost:8000";
    }
    return window.location.origin;
  }
  return "https://app.neurolonic.com";
};

const BACKEND_ORIGIN = normalizeBaseUrl(resolveBackendOrigin());
const API_ROOT = `${BACKEND_ORIGIN}/api`;
const IS_LOCAL_BACKEND =
  /^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?$/i.test(BACKEND_ORIGIN);
const TRANSCRIBE_ENDPOINT = `${IS_LOCAL_BACKEND ? BACKEND_ORIGIN : API_ROOT}/transcribe`;

const resolvePredictEndpoint = () => {
  const envPredict = normalizeBaseUrl(process.env.REACT_APP_PREDICT_URL);
  if (envPredict) {
    return `${envPredict}/predict`;
  }
  if (IS_LOCAL_BACKEND) {
    return "http://localhost:5000/predict";
  }
  return `${BACKEND_ORIGIN}/app/predict`;
};

const PREDICT_ENDPOINT = resolvePredictEndpoint();

// tiny reusable UI bits (progress + sparkline) inspired by TakeCareAgency
function ProgressBar({ label, value = 0, color = "#6fcf97" }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <div className="su-prog">
      <div className="su-prog-row">
        <span>{label}</span>
        <span className="su-prog-pct">{pct}%</span>
      </div>
      <div className="su-prog-track">
        <div
          className="su-prog-fill"
          style={{ width: pct + "%", background: color }}
        />
      </div>
    </div>
  );
}

export default function SchoolUniversityHome({ userDetails }) {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [_loading, setLoading] = useState(true);

  const extractList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.data)) return payload.data;
    if (payload && Array.isArray(payload.results)) return payload.results;
    return [];
  };

  const normalizeId = (value) =>
    value === undefined || value === null || value === ""
      ? null
      : String(value);

  const mapStaffRecord = useCallback((s) => {
    const id = normalizeId(s.staff_id || s.id);
    if (!id) return null;
    return {
      id,
      name: s.name,
      role: s.role || s.designation || "Psychologist",
      contact: s.contact || "",
      avatar: s.profile_picture || DEFAULT_AVATAR,
      profile_picture: s.profile_picture || null,
      last_session: s.last_session || null,
      eve: {
        calm: Number(s?.eve?.calm ?? 0),
        happy: Number(s?.eve?.happy ?? 0),
        stressed: Number(s?.eve?.stressed ?? 0),
        demotivated: Number(s?.eve?.demotivated ?? 0),
        anxious: Number(s?.eve?.anxious ?? 0),
        hurt: Number(s?.eve?.hurt ?? 0),
      },
      burnout_score: Number(s.burnout_score ?? 0),
    };
  }, []);

  const mapStudentRecord = useCallback((s) => {
    const id = normalizeId(s.student_id || s.id);
    if (!id) return null;
    return {
      id,
      name: s.name,
      class: s.class || s.class_name || s.grade || "",
      section: s.section || "",
      avatar: s.profile_picture || DEFAULT_AVATAR,
      profile_picture: s.profile_picture || null,
      age: s.age ?? null,
      gender: s.gender ?? null,
      last_session: s.last_session || null,
      eve: {
        calm: Number(s?.eve?.calm ?? 0),
        happy: Number(s?.eve?.happy ?? 0),
        stressed: Number(s?.eve?.stressed ?? 0),
        demotivated: Number(s?.eve?.demotivated ?? 0),
        anxious: Number(s?.eve?.anxious ?? 0),
        hurt: Number(s?.eve?.hurt ?? 0),
      },
      burnout_score: Number(s.burnout_score ?? 0),
      prediction: s.prediction || { weekly: [], monthly: [] },
      assigned_staff_id: s.assigned_staff_id || s.staff_id || null,
      contact: s.contact || "",
    };
  }, []);

  // Tabs: 'students' (default) or 'staff'
  const [activeTab, setActiveTab] = useState("students");

  // CRUD modals / forms
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [studentForm, setStudentForm] = useState({
    name: "",
    class_name: "",
    section: "",
    age: "",
    gender: "Other",
    contact: "",
  });
  const [savingStudent, setSavingStudent] = useState(false);

  const [showStaffModal, setShowStaffModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({
    name: "",
    designation: "Psychologist",
    contact: "",
  });
  const [savingStaff, setSavingStaff] = useState(false);
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEmotionBreakdown, setShowEmotionBreakdown] = useState(false);

  // session / record state (5 x 8s segments like TakeCareAgency)
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [micClicks, setMicClicks] = useState(0);
  const [segmentRecording, setSegmentRecording] = useState(false);
  const [continuousRecording, setContinuousRecording] = useState(false);
  const [_segmentResults, setSegmentResults] = useState([]);
  const [timer, setTimer] = useState(0);
  const [running, setRunning] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const activeSessionStudentRef = useRef(null);
  const [lastLatencyMs, setLastLatencyMs] = useState(null);
  const [lastDataSource, setLastDataSource] = useState(null);

  // Notes state
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [liveNoteText, setLiveNoteText] = useState("");
  const [noteRecState, setNoteRecState] = useState("idle"); // idle | recording | transcribing | error
  const [noteError, setNoteError] = useState("");
  const [notesBySession, setNotesBySession] = useState({});
  const [notesByEntity, setNotesByEntity] = useState({}); // Store notes by entity ID (like TakeCareAgencyDashboard)

  // Note transcription recording refs (like TakeCareAgencyDashboard)
  const noteMediaRecorderRef = useRef(null);
  const noteAudioChunksRef = useRef([]);
  const noteStreamRef = useRef(null);

  const refreshStudentsList = useCallback(async () => {
    try {
      const userId =
        userDetails?.user_id || localStorage.getItem("neuro_user_id");
      const userIdParam = userId
        ? `?created_by=${encodeURIComponent(userId)}`
        : "";
      const sRes = await authFetch(`${API_ROOT}/school/students${userIdParam}`, { headers: authHeaders() })
        .then((r) => r.json())
        .catch(() => ({ status_code: 500 }));
      const studentsData = extractList(sRes);
      if (!studentsData || !studentsData.length) return [];
      return studentsData
        .map((s) => {
          const id = s.student_id || s.id;
          if (!id) return null;
          return {
            id: String(id),
            name: s.name,
            class: s.class || s.class_name || s.grade || "",
            section: s.section || "",
            avatar: s.profile_picture || DEFAULT_AVATAR,
            profile_picture: s.profile_picture || null,
            age: s.age ?? null,
            gender: s.gender ?? null,
            last_session: s.last_session || null,
            eve: {
              calm: Number(s?.eve?.calm ?? 0),
              stressed: Number(s?.eve?.stressed ?? 0),
              demotivated: Number(s?.eve?.demotivated ?? 0),
              anxious: Number(s?.eve?.anxious ?? 0),
              hurt: Number(s?.eve?.hurt ?? 0),
            },
            burnout_score: Number(s.burnout_score ?? 0),
            prediction: s.prediction || { weekly: [], monthly: [] },
            assigned_staff_id: s.assigned_staff_id || s.staff_id || null,
            contact: s.contact || "",
            email: s.contact || s.email || "",
          };
        })
        .filter(Boolean);
    } catch (e) {
      console.error("Failed to refresh students", e);
      return [];
    }
  }, [userDetails]);

  const studentImportProps = useStudentImportExport({
    students,
    userDetails,
    t,
    onImportComplete: useCallback(async () => {
      const refreshed = await refreshStudentsList();
      setStudents(refreshed);
    }, [refreshStudentsList]),
  });

  const staffImportProps = useStaffImportExport({
    staff,
    userDetails,
    t,
    onImportComplete: useCallback(async () => {
      const userId =
        userDetails?.user_id || localStorage.getItem("neuro_user_id");
      const userIdParam = userId
        ? `?created_by=${encodeURIComponent(userId)}`
        : "";
      const stRes = await authFetch(`${API_ROOT}/school/staff${userIdParam}`, { headers: authHeaders() })
        .then((r) => r.json())
        .catch(() => ({ status_code: 500 }));
      const staffData = extractList(stRes);
      setStaff((staffData || []).map(mapStaffRecord).filter(Boolean));
    }, [userDetails, mapStaffRecord]),
  });

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        // follow existing API patterns - include user_id for multi-tenancy
        const userId =
          userDetails?.user_id || localStorage.getItem("neuro_user_id");
        const userIdParam = userId
          ? `?created_by=${encodeURIComponent(userId)}`
          : "";
        const [sRes, stRes] = await Promise.all([
          dashboardApi.getSchoolStudents?.(userId) ||
            authFetch(`${API_ROOT}/school/students${userIdParam}`, { headers: authHeaders() })
              .then((r) => r.json())
              .catch(() => ({ status_code: 500 })),
          dashboardApi.getSchoolStaff?.(userId) ||
            authFetch(`${API_ROOT}/school/staff${userIdParam}`, { headers: authHeaders() })
              .then((r) => r.json())
              .catch(() => ({ status_code: 500 })),
        ]);
        const studentsData = extractList(sRes);
        const staffData = extractList(stRes);
        if (!mounted) return;
        setStudents((studentsData || []).map(mapStudentRecord).filter(Boolean));
        setStaff((staffData || []).map(mapStaffRecord).filter(Boolean));
      } catch (e) {
        console.error("Failed to load school data", e);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Load once on mount - userDetails is available from props

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTimer((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  // Predict mapping (reuse TakeCareAgency logic)
  function mapApiResponseToMetrics(apiResult) {
    const res =
      (apiResult &&
        (apiResult.RES || apiResult.res || apiResult.data || apiResult)) ||
      {};
    const top5String = res.top_5 || "";
    const scores = {};
    EMOTION_KEYS.forEach((em) => {
      try {
        const re = new RegExp(`${em}\\s+(\\d+(?:\\.\\d+)?)%`, "i");
        const m = top5String.match(re);
        scores[em] = m ? parseFloat(m[1]) / 100 : 0;
      } catch {
        scores[em] = 0;
      }
    });
    const calm = scores.happy ?? 0;
    const stressed = scores.angry ?? 0;
    const demotivated = scores.sad ?? 0;
    const anxious = scores.fear ?? 0;
    const hurt = scores.disgust ?? 0;
    const burnout =
      Math.round(
        ((scores.angry || 0) + (scores.sad || 0) + (scores.fear || 0)) * 100,
      ) || 0;
    return { calm, stressed, demotivated, anxious, hurt, burnout };
  }

  async function callPredictAPI(audioBlob) {
    try {
      const formData = new FormData();
      formData.append("file", audioBlob, "recording.wav");
      const startTs = performance?.now ? performance.now() : Date.now();
      const res = await fetch(PREDICT_ENDPOINT, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error(`Predict failed ${res.status}`);
      const json = await res.json();
      const endTs = performance?.now ? performance.now() : Date.now();
      setLastLatencyMs(Math.round(endTs - startTs));
      setLastDataSource("api");
      return mapApiResponseToMetrics(json);
    } catch (err) {
      console.error("Predict API error", err);
      setLastDataSource("error");
      setLastLatencyMs(null);
      return null;
    }
  }

  // aggregate 5 segments
  function averageSegments(results) {
    if (!results || results.length === 0) return null;
    const keys = ["calm", "stressed", "demotivated", "anxious", "hurt"];
    const avg = keys.reduce((acc, k) => {
      acc[k] =
        results.reduce((s, r) => s + Number(r[k] || 0), 0) / results.length;
      return acc;
    }, {});
    const burnRaw =
      results.reduce((s, r) => s + Number(r.burnout || 0), 0) / results.length;
    const burn = Number.isFinite(burnRaw) ? Math.round(burnRaw) : 0;
    return { eve: avg, burnout: burn };
  }

  const refreshEntityEmotion = useCallback(
    async (entityType, entityIdInput, sessionId) => {
      try {
        if (!entityType || !entityIdInput) return;
        const entityId = String(entityIdInput);
        const params = sessionId
          ? `?session_id=${encodeURIComponent(sessionId)}`
          : "";
        const endpoint =
          entityType === "staff"
            ? `${API_ROOT}/school/staff/${entityId}/emotions${params}`
            : `${API_ROOT}/school/students/${entityId}/emotions${params}`;
        const resp = await authFetch(endpoint, { headers: authHeaders() }).then((r) =>
          r.json().catch(() => ({})),
        );
        const items =
          (resp?.status_code === 200 ? resp.data : resp?.data) || resp || [];
        if (!Array.isArray(items) || items.length === 0) return;
        const latest = items[0] || {};
        const eve = {
          calm: Number(latest?.calm ?? 0),
          stressed: Number(latest?.stressed ?? 0),
          anxious: Number(latest?.anxious ?? 0),
          demotivated: Number(latest?.demotivated ?? 0),
          hurt: Number(latest?.hurt ?? 0),
        };
        const burnoutScore = Number(latest?.burnout_score ?? 0);
        const lastSessionDate =
          latest?.session_date || latest?.created_at || null;
        if (entityType === "staff") {
          setStaff((prev) =>
            prev.map((s) => {
              if (String(s.id) !== entityId) return s;
              return {
                ...s,
                eve: { ...s.eve, ...eve },
                burnout_score: burnoutScore,
                last_session: lastSessionDate
                  ? lastSessionDate.split("T")[0]
                  : s.last_session,
              };
            }),
          );
        } else {
          setStudents((prev) =>
            prev.map((s) => {
              if (String(s.id) !== entityId) return s;
              return {
                ...s,
                eve: { ...s.eve, ...eve },
                burnout_score: burnoutScore,
                last_session: lastSessionDate
                  ? lastSessionDate.split("T")[0]
                  : s.last_session,
              };
            }),
          );
        }
      } catch (err) {
        console.error("Failed to refresh emotion data", err);
      }
    },
    [setStaff, setStudents],
  );

  async function saveSessionToBackend(
    targetEntity,
    avgResults,
    durationSeconds,
  ) {
    try {
      if (!targetEntity) return;
      const nowDate = new Date().toISOString().split("T")[0];
      if (targetEntity.type === "staff") {
        const sessionResp = await authFetch(`${API_ROOT}/school/staff/session`, {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            staff_id: String(targetEntity.id),
            session_date: nowDate,
            duration: `${durationSeconds} seconds`,
          }),
        }).then((r) => r.json().catch(() => ({})));
        const sessionId =
          sessionResp?.data?.session_id || sessionResp?.session_id;
        if (!sessionId) {
          console.error("❌ Failed to create staff session", sessionResp);
          return;
        }
        const sessionIdStr = String(sessionId);
        setCurrentSessionId(sessionIdStr);

        await authFetch(`${API_ROOT}/school/staff/emotion`, {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            staff_id: String(targetEntity.id),
            session_id: sessionIdStr,
            calm: Number((avgResults.eve.calm * 100).toFixed(2)),
            stressed: Number((avgResults.eve.stressed * 100).toFixed(2)),
            anxious: Number((avgResults.eve.anxious * 100).toFixed(2)),
            demotivated: Number((avgResults.eve.demotivated * 100).toFixed(2)),
            hurt: Number((avgResults.eve.hurt * 100).toFixed(2)),
            burnout_score: Number.isFinite(avgResults.burnout)
              ? Math.round(avgResults.burnout)
              : 0,
          }),
        }).catch((e) => console.error("Failed to save staff emotion", e));
        await refreshEntityEmotion("staff", targetEntity.id, sessionIdStr);
      } else {
        const sessionResp = await authFetch(`${API_ROOT}/school/session`, {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            student_id: String(targetEntity.id),
            staff_id: String(userDetails?.user_id || ""),
            session_date: nowDate,
            duration: `${durationSeconds} seconds`,
          }),
        }).then((r) => r.json().catch(() => ({})));
        const sessionId =
          sessionResp?.data?.session_id ||
          sessionResp?.session_id ||
          sessionResp?.session?.id;
        if (!sessionId) {
          console.error("❌ Failed to create student session", sessionResp);
          return;
        }
        const sessionIdStr = String(sessionId);
        setCurrentSessionId(sessionIdStr);

        await authFetch(`${API_ROOT}/school/emotion`, {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            student_id: String(targetEntity.id),
            session_id: sessionIdStr,
            calm: Number((avgResults.eve.calm * 100).toFixed(2)),
            stressed: Number((avgResults.eve.stressed * 100).toFixed(2)),
            anxious: Number((avgResults.eve.anxious * 100).toFixed(2)),
            demotivated: Number((avgResults.eve.demotivated * 100).toFixed(2)),
            hurt: Number((avgResults.eve.hurt * 100).toFixed(2)),
            burnout_score: Number.isFinite(avgResults.burnout)
              ? Math.round(avgResults.burnout)
              : 0,
          }),
        }).catch((e) => console.error("Failed to save emotion", e));
        await refreshEntityEmotion("student", targetEntity.id, sessionIdStr);
      }
    } catch (e) {
      console.error("Failed to save session to backend", e);
    }
  }

  async function startRecordingSegment() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size) audioChunksRef.current.push(ev.data);
      };
      mr.onstop = async () => {
        try {
          const blob = new Blob(audioChunksRef.current, { type: "audio/wav" });
          const metrics = await callPredictAPI(blob);
          setSegmentResults((prev) => {
            const next = [...prev, metrics];
            if (next.length === 5) {
              const avg = averageSegments(next);
              if (avg) {
                const targetEntity =
                  activeSessionStudentRef.current || selectedEntity;
                saveSessionToBackend(targetEntity, avg, timer);
                if (targetEntity?.type === "staff") {
                  setStaff((prev) =>
                    prev.map((s) =>
                      s.id !== targetEntity.id
                        ? s
                        : {
                            ...s,
                            last_session: new Date().toISOString().slice(0, 10),
                            eve: { ...s.eve, ...avg.eve },
                            burnout_score: avg.burnout,
                          },
                    ),
                  );
                } else {
                  setStudents((prev) =>
                    prev.map((s) =>
                      s.id !== targetEntity.id
                        ? s
                        : {
                            ...s,
                            last_session: new Date().toISOString().slice(0, 10),
                            eve: { ...s.eve, ...avg.eve },
                            burnout_score: avg.burnout,
                          },
                    ),
                  );
                }
              }
              setRunning(false);
              setMicClicks(0);
              setTimer(0);
              activeSessionStudentRef.current = null;
              return [];
            }
            return next;
          });
        } catch (err) {
          console.error("Error processing segment", err);
          setRunning(false);
          setTimer(0);
        }
      };
      mr.start();
      setRunning(true);
      setSegmentRecording(true);
      setTimeout(() => {
        try {
          if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state === "recording"
          ) {
            mediaRecorderRef.current.stop();
          }
        } catch (err) {
          console.error("Error stopping recording", err);
        }
        setSegmentRecording(false);
      }, 8000);
    } catch (e) {
      console.error("Recording failed", e);
      alert(t("Microphone permission required or recording error."));
      setSegmentRecording(false);
      setRunning(false);
      setTimer(0);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    }
  }

  function handleMicClick() {
    if (!selectedEntity) {
      alert(t("Select a student or staff member first"));
      return;
    }
    if (micClicks >= 5 || segmentRecording) return;
    if (micClicks === 0) {
      setTimer(0);
      activeSessionStudentRef.current = selectedEntity;
    }
    setMicClicks((c) => c + 1);
    startRecordingSegment();
  }

  // Continuous recording: record 40 seconds at once
  async function startContinuousRecording() {
    if (!selectedEntity) {
      alert(t("Select a student or staff member first"));
      return;
    }
    if (segmentRecording || continuousRecording) return;

    try {
      activeSessionStudentRef.current = selectedEntity;
      setTimer(0);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];

      setContinuousRecording(true);
      setRunning(true);

      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size) audioChunksRef.current.push(ev.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/wav" });
        const metrics = await callPredictAPI(blob);

        const targetEntity = activeSessionStudentRef.current || selectedEntity;
        if (metrics) {
          saveSessionToBackend(targetEntity, metrics, timer);
          if (targetEntity?.type === "staff") {
            setStaff((prev) =>
              prev.map((s) =>
                s.id !== targetEntity.id
                  ? s
                  : {
                      ...s,
                      last_session: new Date().toISOString().slice(0, 10),
                      last_session_duration: timer,
                      eve: { ...s.eve, ...metrics.eve },
                      burnout_score: metrics.burnout,
                    },
              ),
            );
          } else {
            setStudents((prev) =>
              prev.map((s) =>
                s.id !== targetEntity.id
                  ? s
                  : {
                      ...s,
                      last_session: new Date().toISOString().slice(0, 10),
                      last_session_duration: timer,
                      eve: { ...s.eve, ...metrics.eve },
                      burnout_score: metrics.burnout,
                    },
              ),
            );
          }
        }
        setRunning(false);
        setTimer(0);
        setContinuousRecording(false);
        activeSessionStudentRef.current = null;
      };

      mr.start();
      setRunning(true);
      setSegmentRecording(true);
      // auto-stop after 40s
      setTimeout(() => {
        try {
          if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state === "recording"
          )
            mediaRecorderRef.current.stop();
        } catch {}
        setSegmentRecording(false);
        setContinuousRecording(false);
      }, 40000);
    } catch (e) {
      console.error("Continuous recording failed", e);
      alert(t("Microphone permission required or recording error."));
      setRunning(false);
      setTimer(0);
      setSegmentRecording(false);
      setContinuousRecording(false);
      activeSessionStudentRef.current = null;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    }
  }

  // Notes helpers: load, save, delete
  async function loadSessionNotes(sessionId) {
    if (!sessionId) return;
    try {
      const resp = await authFetch(
        `${API_ROOT}/school/session/${sessionId}/notes`,
        { headers: authHeaders() },
      ).then((r) => r.json().catch(() => ({})));
      const notes =
        (resp?.status_code === 200 ? resp.data : resp?.data) || resp || [];
      const list = Array.isArray(notes)
        ? notes.map((n) => ({
            note_id: n.note_id || n.id || n.noteId,
            staff_id: n.staff_id,
            staff_name: n.staff_name || n.staffName || "Staff",
            note_text: n.note_text || n.text || "",
            text: n.note_text || n.text || "",
            created_at: n.created_at,
          }))
        : [];
      setNotesBySession((prev) => ({ ...prev, [sessionId]: list }));
    } catch (e) {
      console.error("Failed to load session notes", e);
    }
  }

  const displayUserName = useMemo(() => {
    const details = userDetails || {};
    const candidates = [
      details.full_name,
      details.fullName,
      details.display_name,
      details.displayName,
      details.name,
    ].filter(Boolean);
    if (candidates.length > 0) {
      const firstNonEmpty = candidates.find(
        (v) => typeof v === "string" && v.trim().length > 0,
      );
      if (firstNonEmpty) return firstNonEmpty.trim();
    }
    const storedDisplayName = (() => {
      try {
        const value = localStorage.getItem("neuro_display_name");
        if (value && value.trim().length > 0) return value.trim();
      } catch (_) {}
      return null;
    })();
    if (storedDisplayName) return storedDisplayName;

    const fallback = details.user_name || details.username || details.email;
    if (typeof fallback === "string" && fallback.trim().length > 0) {
      const trimmed = fallback.trim();
      if (trimmed.includes("@")) return trimmed.split("@")[0];
      return trimmed;
    }
    return null;
  }, [userDetails]);

  const [showReportModal, setShowReportModal] = useState(false);
  const [reportRange, setReportRange] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportError, setReportError] = useState("");
  const [historyState, setHistoryState] = useState({
    entityId: null,
    loading: false,
    error: "",
    data: [],
  });

  async function saveNote() {
    const text = (liveNoteText || "").trim();
    if (!text) {
      setNoteError(t("Nothing to save"));
      setTimeout(() => setNoteError(""), 2000);
      return;
    }
    const sid = currentSessionId;
    if (!sid) {
      setNoteError(t("Finish a recording session first to attach notes."));
      setTimeout(() => setNoteError(""), 2500);
      return;
    }
    try {
      const entityId = selectedEntity?.id ? String(selectedEntity.id) : null;
      const staffForNote =
        selectedEntity?.type === "staff"
          ? entityId
          : selectedRecord?.assigned_staff_id || selectedRecord?.staff_id || null;
      const payload = { staff_id: staffForNote, note_text: text };
      const res = await authFetch(`${API_ROOT}/school/session/${sid}/note`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).then((r) => r.json().catch(() => ({})));
      const created =
        (res?.status_code === 200 ? res.data : res?.data) || res || {};
      if (created?.note_id || created?.id) {
        await loadSessionNotes(sid);
        if (entityId && selectedEntity?.type) {
          try {
            const endpoint =
              selectedEntity.type === "student"
                ? `${API_ROOT}/school/students/${entityId}/sessions`
                : `${API_ROOT}/school/staff/${entityId}/sessions`;
            const sessionsResp = await authFetch(endpoint, { headers: authHeaders() }).then((r) =>
              r.json().catch(() => ({})),
            );
            const sessions =
              (sessionsResp?.status_code === 200
                ? sessionsResp.data
                : sessionsResp?.data) ||
              sessionsResp ||
              [];
            if (Array.isArray(sessions) && sessions.length > 0) {
              const notePromises = sessions
                .filter((s) => s.session_id || s.id)
                .map((s) => {
                  const sessionId = s.session_id || s.id;
                  return fetch(`${API_ROOT}/school/session/${sessionId}/notes`)
                    .then((r) => r.json().catch(() => ({})))
                    .then((resp) => ({ sessionId, resp }))
                    .catch(() => ({ sessionId, resp: null }));
                });
              const results = await Promise.all(notePromises);
              const allNotes = [];
              for (const { sessionId, resp } of results) {
                if (
                  resp &&
                  resp.status_code === 200 &&
                  Array.isArray(resp.data)
                ) {
                  for (const n of resp.data) {
                    allNotes.push({
                      note_id: n.note_id || n.id || n.noteId,
                      session_id: String(sessionId),
                      staff_id: n.staff_id,
                      staff_name:
                        n.staff_name !== null && n.staff_name !== undefined
                          ? n.staff_name
                          : "Staff",
                      note_text: n.note_text || n.text || "",
                      text: n.note_text || n.text || "",
                      created_at: n.created_at,
                    });
                  }
                }
              }
              allNotes.sort((a, b) => {
                const dateA = a.created_at
                  ? new Date(a.created_at).getTime()
                  : 0;
                const dateB = b.created_at
                  ? new Date(b.created_at).getTime()
                  : 0;
                return dateB - dateA;
              });
              setNotesByEntity((prev) => ({ ...prev, [entityId]: allNotes }));
            }
          } catch (err) {
            console.error("Failed to reload all notes after save:", err);
          }
        }
        setLiveNoteText("");
        setNoteError(t("Note saved"));
        setTimeout(() => setNoteError(""), 1800);
      } else {
        throw new Error("No note ID in response");
      }
    } catch (e) {
      console.error("Failed to save note", e);
      setNoteError(t("Failed to save note. Try again."));
      setTimeout(() => setNoteError(""), 2500);
    }
  }

  async function deleteNote(sessionId, noteId) {
    if (!noteId) return;
    try {
      await authFetch(`${API_ROOT}/school/session/notes/${noteId}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then((r) => r.json().catch(() => ({})));
      if (sessionId) {
        await loadSessionNotes(sessionId);
      }
      if (selectedEntity?.id && selectedEntity?.type) {
        const entityId = String(selectedEntity.id);
        const endpoint =
          selectedEntity.type === "student"
            ? `${API_ROOT}/school/students/${entityId}/sessions`
            : `${API_ROOT}/school/staff/${entityId}/sessions`;
        try {
          const sessionsResp = await authFetch(endpoint, { headers: authHeaders() }).then((r) =>
            r.json().catch(() => ({})),
          );
          const sessions =
            (sessionsResp?.status_code === 200
              ? sessionsResp.data
              : sessionsResp?.data) ||
            sessionsResp ||
            [];
          if (Array.isArray(sessions) && sessions.length > 0) {
            const notePromises = sessions
              .filter((s) => s.session_id || s.id)
              .map((s) => {
                const sid = s.session_id || s.id;
                return authFetch(`${API_ROOT}/school/session/${sid}/notes`, { headers: authHeaders() })
                  .then((r) => r.json().catch(() => ({})))
                  .then((resp) => ({ sessionId: String(sid), resp }))
                  .catch(() => ({ sessionId: String(sid), resp: null }));
              });
            const results = await Promise.all(notePromises);
            const allNotes = [];
            for (const { sessionId, resp } of results) {
              if (
                resp &&
                resp.status_code === 200 &&
                Array.isArray(resp.data)
              ) {
                for (const n of resp.data) {
                  allNotes.push({
                    note_id: n.note_id || n.id || n.noteId,
                    session_id: String(sessionId),
                    staff_id: n.staff_id,
                    staff_name: n.staff_name || "Staff",
                    note_text: n.note_text || n.text || "",
                    text: n.note_text || n.text || "",
                    created_at: n.created_at,
                  });
                }
              }
            }
            allNotes.sort((a, b) => {
              const dateA = a.created_at
                ? new Date(a.created_at).getTime()
                : 0;
              const dateB = b.created_at
                ? new Date(b.created_at).getTime()
                : 0;
              return dateB - dateA;
            });
            setNotesByEntity((prev) => ({ ...prev, [entityId]: allNotes }));
          }
        } catch (err) {
          console.error("Failed to reload all notes after delete:", err);
        }
      }
    } catch (e) {
      console.error("Failed to delete note", e);
      setNoteError(t("Failed to delete note"));
      setTimeout(() => setNoteError(""), 2000);
    }
  }

  // Speech-to-text transcription
  async function transcribeNoteAudio(audioBlob) {
    try {
      setNoteRecState("transcribing");
      setNoteError("");
      // Prefer server-side transcription in production (like TakeCareAgencyDashboard)
      const useServer = true;
      let text = "";
      if (useServer) {
        const fd = new FormData();
        fd.append("file", audioBlob, "note-recording.webm");
        fd.append("language", "en");
        const r = await authFetch(TRANSCRIBE_ENDPOINT, {
          method: "POST",
          headers: authHeaders(),
          body: fd,
        });
        if (!r.ok) {
          const t = await r.text().catch(() => "");
          throw new Error(
            `Server transcription failed (${r.status}): ${t || r.statusText}`,
          );
        }
        const j = await r.json();
        if (
          j &&
          j.status_code === 200 &&
          j.data &&
          typeof j.data.text === "string"
        ) {
          text = j.data.text;
        } else {
          // Show error message from server if available
          const errorMsg = j?.message || j?.detail || "Transcription failed";
          throw new Error(errorMsg);
        }
      } else {
        // Client-side fallback (not used by default)
        const fromProcess =
          typeof process !== "undefined" && process.env
            ? process.env.OPENAI_API_KEY || process.env.REACT_APP_OPENAI_API_KEY
            : undefined;
        const fromWindow =
          typeof window !== "undefined" && window.OPENAI_API_KEY
            ? window.OPENAI_API_KEY
            : undefined;
        const apiKey = fromProcess || fromWindow;
        if (!apiKey) throw new Error("Missing OPENAI_API_KEY");
        const form = new FormData();
        form.append("file", audioBlob, "note-recording.webm");
        form.append("model", "whisper-1");
        const res = await fetch(
          "https://api.openai.com/v1/audio/transcriptions",
          {
            method: "POST",
            headers: { Authorization: `Bearer ${apiKey}` },
            body: form,
          },
        );
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(
            `Transcription failed (${res.status}): ${t || res.statusText}`,
          );
        }
        const data = await res.json();
        text =
          (data && (data.text || data.text === "" ? data.text : data.result)) ||
          "";
      }
      if (typeof text === "string") {
        setLiveNoteText((prev) =>
          prev ? prev + (prev.endsWith("\n") ? "" : "\n") + text : text,
        );
      }
      setNoteRecState("idle");
    } catch (e) {
      console.error("Note transcription error:", e);
      setNoteError(e?.message || t("Transcription error"));
      setNoteRecState("error");
      setTimeout(() => setNoteRecState("idle"), 2500);
    }
  }

  async function startNoteRecording() {
    if (noteRecState === "recording" || noteRecState === "transcribing") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      noteStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : undefined;
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      noteMediaRecorderRef.current = mr;
      noteAudioChunksRef.current = [];
      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0)
          noteAudioChunksRef.current.push(ev.data);
      };
      mr.onstop = async () => {
        try {
          const blob = new Blob(noteAudioChunksRef.current, {
            type: mimeType || "audio/webm",
          });
          if (noteStreamRef.current)
            noteStreamRef.current.getTracks().forEach((t) => t.stop());
          await transcribeNoteAudio(blob);
        } finally {
          noteAudioChunksRef.current = [];
        }
      };
      mr.start();
      setNoteRecState("recording");
      // Auto-stop after 60 seconds
      setTimeout(() => {
        try {
          if (
            noteMediaRecorderRef.current &&
            noteMediaRecorderRef.current.state === "recording"
          )
            noteMediaRecorderRef.current.stop();
        } catch {}
      }, 60000);
    } catch (e) {
      console.error("Note recording failed:", e);
      setNoteError(t("Microphone access denied or recording failed"));
      setNoteRecState("error");
      setTimeout(() => setNoteRecState("idle"), 2500);
    }
  }

  function stopNoteRecording() {
    try {
      if (
        noteMediaRecorderRef.current &&
        noteMediaRecorderRef.current.state === "recording"
      ) {
        noteMediaRecorderRef.current.stop();
      }
    } catch (e) {
      console.error("Stop note recording failed:", e);
    }
  }

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTimer((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  // Try to resolve a session id for the currently selected entity (student or staff).
  async function resolveAndLoadSessionNotesForSelected(sel) {
    if (!sel) {
      setCurrentSessionId(null);
      return;
    }

    // prefer explicit session fields only (don't treat sel.id as session id)
    const explicit =
      sel.session_id || sel.sessionId || (sel.session && sel.session.id);
    if (explicit) {
      const sid = String(explicit);
      setCurrentSessionId(sid);
      await loadSessionNotes(sid);
      if (sel.type === "student") {
        await refreshEntityEmotion("student", sel.student_id || sel.id, sid);
      } else if (sel.type === "staff") {
        await refreshEntityEmotion("staff", sel.staff_id || sel.id, sid);
      }
      return;
    }

    const studentId =
      sel.student_id || (sel.type === "student" ? sel.id : null);
    const staffId = sel.staff_id || (sel.type === "staff" ? sel.id : null);

    try {
      let sid = null;

      if (studentId) {
        const r = await authFetch(
          `${API_ROOT}/school/students/${studentId}/sessions`,
          { headers: authHeaders() },
        ).then((r) => r.json().catch(() => ({})));
        const arr = (r?.status_code === 200 ? r.data : r?.data) || r || [];
        // Get the most recent session (first one in DESC order from backend)
        if (Array.isArray(arr) && arr.length > 0) {
          sid = arr[0].session_id || arr[0].id || arr[0].session?.id || null;
          if (sid) {
            sid = String(sid);
          }
        }
      }

      if (!sid && staffId) {
        const r = await authFetch(
          `${API_ROOT}/school/staff/${staffId}/sessions`,
          { headers: authHeaders() },
        ).then((r) => r.json().catch(() => ({})));
        const arr = (r?.status_code === 200 ? r.data : r?.data) || r || [];
        // Get the most recent session (first one in DESC order from backend)
        if (Array.isArray(arr) && arr.length > 0) {
          sid = arr[0].session_id || arr[0].id || arr[0].session?.id || null;
          if (sid) {
            sid = String(sid);
          }
        }
      }

      if (sid) {
        // Set session ID and load notes atomically - don't clear until we have the new one
        setCurrentSessionId(sid);
        await loadSessionNotes(sid);
        const entityLookupId =
          studentId || staffId ? String(studentId || staffId) : null;
        const entityType = studentId ? "student" : "staff";
        if (entityLookupId && entityType) {
          await refreshEntityEmotion(entityType, entityLookupId, sid);
        }

        // Also load ALL notes for this entity (like TakeCareAgencyDashboard) so they persist when switching
        const entityIdForNotes = entityLookupId;
        if (entityIdForNotes) {
          // Load all notes for this entity from all sessions
          try {
            let sessionsResp;
            if (entityType === "student") {
              sessionsResp = await authFetch(
                `${API_ROOT}/school/students/${entityIdForNotes}/sessions`,
                { headers: authHeaders() },
              ).then((r) => r.json().catch(() => ({})));
            } else {
              sessionsResp = await authFetch(
                `${API_ROOT}/school/staff/${entityIdForNotes}/sessions`,
                { headers: authHeaders() },
              ).then((r) => r.json().catch(() => ({})));
            }
            const sessions =
              (sessionsResp?.status_code === 200
                ? sessionsResp.data
                : sessionsResp?.data) ||
              sessionsResp ||
              [];
            if (Array.isArray(sessions) && sessions.length > 0) {
              const notePromises = sessions
                .filter((s) => s.session_id || s.id)
                .map((s) => {
                  const sessionId = s.session_id || s.id;
                  return fetch(`${API_ROOT}/school/session/${sessionId}/notes`)
                    .then((r) => r.json().catch(() => ({})))
                    .then((resp) => ({ sessionId, resp }))
                    .catch(() => ({ sessionId, resp: null }));
                });
              const results = await Promise.all(notePromises);
              const allNotes = [];
              for (const { sessionId, resp } of results) {
                if (
                  resp &&
                  resp.status_code === 200 &&
                  Array.isArray(resp.data)
                ) {
                  for (const n of resp.data) {
                    allNotes.push({
                      note_id: n.note_id || n.id || n.noteId,
                      session_id: String(sessionId),
                      staff_id: n.staff_id,
                      staff_name:
                        n.staff_name !== null && n.staff_name !== undefined
                          ? n.staff_name
                          : n.staffName !== null && n.staffName !== undefined
                            ? n.staffName
                            : "Staff",
                      note_text: n.note_text || n.text || "",
                      text: n.note_text || n.text || "",
                      created_at: n.created_at,
                    });
                  }
                }
              }
              allNotes.sort((a, b) => {
                const dateA = a.created_at
                  ? new Date(a.created_at).getTime()
                  : 0;
                const dateB = b.created_at
                  ? new Date(b.created_at).getTime()
                  : 0;
                return dateB - dateA;
              });
              setNotesByEntity((prev) => ({
                ...prev,
                [entityIdForNotes]: allNotes,
              }));
            }
          } catch (err) {
            console.error(
              `[resolveAndLoadSessionNotesForSelected] Failed to load all notes:`,
              err,
            );
          }
        }
      } else {
        // Only clear if no session found
        setCurrentSessionId(null);
      }
    } catch (e) {
      console.error("[resolveAndLoadSessionNotesForSelected] Error:", e);
      setCurrentSessionId(null);
    }
  }

  // selected record (student OR staff) used by UI — declare early to avoid TDZ when effects reference it
  const selectedRecord = useMemo(() => {
    if (!selectedEntity) return null;
    return selectedEntity.type === "student"
      ? students.find((s) => s.id === selectedEntity.id) || null
      : staff.find((s) => s.id === selectedEntity.id) || null;
  }, [selectedEntity, students, staff]);

  // Cleanup note recording when switching entities (like TakeCareAgencyDashboard)
  useEffect(() => {
    try {
      // Stop note recording if switching entities
      if (
        noteMediaRecorderRef.current &&
        noteMediaRecorderRef.current.state === "recording"
      ) {
        noteMediaRecorderRef.current.stop();
      }
      if (noteStreamRef.current) {
        noteStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      setNoteRecState("idle");
      setNoteError("");
    } catch (e) {
      console.error("Error cleaning up note recording:", e);
    }
  }, [selectedEntity?.id]);

  // Load all notes for entity whenever selectedEntity changes (like TakeCareAgencyDashboard)
  useEffect(() => {
    if (!selectedEntity?.id) {
      return;
    }

    const entityId = String(selectedEntity.id); // Ensure string consistency
    const entityType = selectedEntity.type;

    async function loadAllNotesForEntity() {
      try {
        let sessionsResp;
        if (entityType === "student") {
          sessionsResp = await authFetch(
            `${API_ROOT}/school/students/${entityId}/sessions`,
            { headers: authHeaders() },
          ).then((r) => r.json().catch(() => ({})));
        } else if (entityType === "staff") {
          sessionsResp = await authFetch(
            `${API_ROOT}/school/staff/${entityId}/sessions`,
            { headers: authHeaders() },
          ).then((r) => r.json().catch(() => ({})));
        } else {
          return;
        }

        const sessions =
          (sessionsResp?.status_code === 200
            ? sessionsResp.data
            : sessionsResp?.data) ||
          sessionsResp ||
          [];
        const latestSessionId =
          Array.isArray(sessions) && sessions.length > 0
            ? String(
                sessions[0].session_id ||
                  sessions[0].id ||
                  sessions[0].session?.id ||
                  "",
              )
            : null;
        await refreshEntityEmotion(
          entityType,
          entityId,
          latestSessionId || undefined,
        );

        if (Array.isArray(sessions) && sessions.length > 0) {
              const notePromises = sessions
                .filter((s) => s.session_id || s.id)
                .map((s) => {
                  const sessionId = s.session_id || s.id;
                  return authFetch(`${API_ROOT}/school/session/${sessionId}/notes`, { headers: authHeaders() })
                    .then((r) => r.json().catch(() => ({})))
                    .then((resp) => ({ sessionId, resp }))
                    .catch(() => ({ sessionId, resp: null }));
                });

          const results = await Promise.all(notePromises);
          const allNotes = [];

          for (const { sessionId, resp } of results) {
            if (resp && resp.status_code === 200 && Array.isArray(resp.data)) {
              for (const n of resp.data) {
                allNotes.push({
                  note_id: n.note_id || n.id || n.noteId,
                  session_id: String(sessionId),
                  staff_id: n.staff_id,
                  staff_name:
                    n.staff_name !== null && n.staff_name !== undefined
                      ? n.staff_name
                      : n.staffName !== null && n.staffName !== undefined
                        ? n.staffName
                        : "Staff",
                  note_text: n.note_text || n.text || "",
                  text: n.note_text || n.text || "",
                  created_at: n.created_at,
                });
              }
            }
          }

          allNotes.sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return dateB - dateA;
          });

          setNotesByEntity((prev) => ({ ...prev, [entityId]: allNotes }));
        } else {
          // No sessions, but still set empty array so display logic works
          setNotesByEntity((prev) => ({ ...prev, [entityId]: [] }));
        }
      } catch (err) {
        console.error(
          `[useEffect] Failed to load all notes for ${entityType} ${entityId}:`,
          err,
        );
      }
    }

    loadAllNotesForEntity();
  }, [selectedEntity?.id, selectedEntity?.type, refreshEntityEmotion]);

  // ensure we run resolution when selection changes (or on mount if selectedRecord already set)
  useEffect(() => {
    if (!selectedEntity?.id) {
      setCurrentSessionId(null);
      setSegmentResults([]);
      setMicClicks(0);
      setTimer(0);
      setReportRange(null);
      setReportData(null);
      setReportError("");
      return;
    }
    setSegmentResults([]);
    setMicClicks(0);
    setTimer(0);
    setShowReportModal(false);
    setReportRange(null);
    setReportData(null);
    setReportError("");
    resolveAndLoadSessionNotesForSelected(selectedEntity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity?.id, selectedEntity?.type]);

  // Ensure notes are loaded when currentSessionId changes
  useEffect(() => {
    if (currentSessionId && !notesBySession[currentSessionId]) {
      loadSessionNotes(currentSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId]);

  // simple aggregated school-level metrics for summary cards
  const _schoolSummary = useMemo(() => {
    if (!students.length) return { uploaded: 0, avgBurnout: 0, activeToday: 0 };
    const uploaded = students.reduce(
      (s, st) => s + (st.last_session ? 1 : 0),
      0,
    );
    const avgBurnout = Math.round(
      students.reduce((s, st) => s + (st.burnout_score || 0), 0) /
        students.length || 0,
    );
    const activeToday = students.filter(
      (s) => s.last_session === new Date().toISOString().slice(0, 10),
    ).length;
    return { uploaded, avgBurnout, activeToday };
  }, [students]);

  // new UI state for metrics
  const [userMetrics, setUserMetrics] = useState(null);

  // compute metrics whenever the selected record changes
  useEffect(() => {
    const sel = selectedRecord;
    if (!sel) {
      setUserMetrics(null);
      return;
    }
    // selectedRecord.eve values are stored as decimals (0-1) in this app; convert to percentages
    const eve = sel.eve || {};
    const emotionPct = {
      calm: (eve.calm ?? 0) * 100,
      stressed: (eve.stressed ?? 0) * 100,
      anxious: (eve.anxious ?? 0) * 100,
      hurt: (eve.hurt ?? 0) * 100,
      demotivated: (eve.demotivated ?? 0) * 100,
      happy: (eve.happy ?? 0) * 100,
    };
    const metrics = calculateMetrics(
      emotionPct,
      selectedEntity?.type || "student",
    );
    setUserMetrics(metrics);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecord]);

  async function loadReport(rangeKey) {
    if (!selectedEntity?.id) return;
    setReportLoading(true);
    setReportError("");
    setReportData(null);
    try {
      const res = await (dashboardApi.getSchoolEmotionSummary
        ? dashboardApi.getSchoolEmotionSummary(
            selectedEntity.type,
            selectedEntity.id,
            rangeKey,
          )
        : fetch(
            `${API_ROOT}/school/${selectedEntity.type === "staff" ? "staff" : "students"}/${selectedEntity.id}/emotions/summary?range=${encodeURIComponent(rangeKey)}`,
          ).then((r) => r.json().catch(() => ({}))));
      const payload = res?.data || res;
      if (res?.status_code === 200 && payload) {
        setReportData(payload);
      } else if (payload?.range) {
        setReportData(payload);
      } else {
        const msg = res?.message || payload?.message || "Failed to load report";
        setReportError(msg);
        setReportData(null);
      }
    } catch (err) {
      console.error("Failed to load summary report", err);
      setReportError("Failed to load report");
      setReportData(null);
    } finally {
      setReportLoading(false);
    }
  }

  function handleOpenReport(rangeKey) {
    if (!selectedEntity?.id) return;
    setShowReportModal(true);
    setReportRange(rangeKey);
    setReportError("");
    setReportData(null);
    loadReport(rangeKey);
  }

  function handleCloseReport() {
    setShowReportModal(false);
    setReportRange(null);
    setReportError("");
    setReportData(null);
    setReportLoading(false);
  }

  async function toggleHistory() {
    if (!selectedEntity?.id) return;
    if (historyState.entityId === selectedEntity.id) {
      setHistoryState({ entityId: null, loading: false, error: "", data: [] });
      return;
    }
    setHistoryState({
      entityId: selectedEntity.id,
      loading: true,
      error: "",
      data: [],
    });
    try {
      const res = await (dashboardApi.getSchoolEmotionHistory
        ? dashboardApi.getSchoolEmotionHistory(
            selectedEntity.type,
            selectedEntity.id,
          )
        : fetch(
            `${API_ROOT}/school/${selectedEntity.type === "staff" ? "staff" : "students"}/${selectedEntity.id}/emotions`,
          ).then((r) => r.json().catch(() => ({}))));
      const payload = res?.data || res;
      if (Array.isArray(payload)) {
        const enriched = await enrichHistoryWithNotes(payload);
        setHistoryState({
          entityId: selectedEntity.id,
          loading: false,
          error: "",
          data: enriched,
        });
      } else if (payload?.data && Array.isArray(payload.data)) {
        const enriched = await enrichHistoryWithNotes(payload.data);
        setHistoryState({
          entityId: selectedEntity.id,
          loading: false,
          error: "",
          data: enriched,
        });
      } else {
        const msg =
          res?.message || payload?.message || "Failed to load history";
        setHistoryState({
          entityId: selectedEntity.id,
          loading: false,
          error: msg,
          data: [],
        });
      }
    } catch (err) {
      console.error("Failed to load history", err);
      setHistoryState({
        entityId: selectedEntity.id,
        loading: false,
        error: "Failed to load history",
        data: [],
      });
    }
  }

  // Profile picture upload handler
  const handleProfilePictureUpload = async (file, entityType, entityId) => {
    if (!file) return;

    setUploadingProfilePic(true);
    try {
      const compressedBase64 = await compressImage(file);

      const payload = {
        name:
          entityType === "student"
            ? students.find((s) => s.id === entityId)?.name || ""
            : staff.find((s) => s.id === entityId)?.name || "",
        profile_picture: compressedBase64,
      };

      if (entityType === "student") {
        if (studentForm.class_name) payload.class_name = studentForm.class_name;
        if (studentForm.section) payload.section = studentForm.section;
        if (studentForm.age) payload.age = Number(studentForm.age);
        if (studentForm.gender) payload.gender = studentForm.gender;
        if (studentForm.contact) payload.contact = studentForm.contact;
      } else {
        const staffRecord = staff.find((s) => s.id === entityId);
        if (staffRecord) {
          payload.role = staffRecord.role || "Psychologist";
          payload.contact = staffRecord.contact || "";
        }
      }

      const endpoint =
        entityType === "student"
          ? `${API_ROOT}/school/students/${entityId}`
          : `${API_ROOT}/school/staff/${entityId}`;

      const res = await authFetch(endpoint, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to update profile picture");

      // Refresh the data
      const userId =
        userDetails?.user_id || localStorage.getItem("neuro_user_id");
      const userIdParam = userId
        ? `?created_by=${encodeURIComponent(userId)}`
        : "";

      if (entityType === "student") {
        const listRes = await authFetch(
          `${API_ROOT}/school/students${userIdParam}`,
          { headers: authHeaders() },
        ).then((r) => r.json().catch(() => ({})));
        const studentsData = extractList(listRes);
        setStudents((studentsData || []).map(mapStudentRecord).filter(Boolean));
      } else {
        const listRes = await authFetch(
          `${API_ROOT}/school/staff${userIdParam}`,
          { headers: authHeaders() },
        ).then((r) => r.json().catch(() => ({})));
        const staffData = extractList(listRes);
        setStaff((staffData || []).map(mapStaffRecord).filter(Boolean));
      }
    } catch (error) {
      console.error("Failed to upload profile picture:", error);
      alert(
        t("Failed to upload profile picture: ") +
          (error.message || "Unknown error"),
      );
    } finally {
      setUploadingProfilePic(false);
    }
  };

  // Student CRUD functions (call dashboardApi or fallback)
  async function saveStudent() {
    if (savingStudent) return;
    const name = (studentForm.name || "").trim();
    if (!name) return alert(t("Please enter student name"));
    setSavingStudent(true);
    try {
      const payload = {
        name,
        class_name: studentForm.class_name || studentForm.class || "",
        section: studentForm.section || "",
        age: studentForm.age ? Number(studentForm.age) : null,
        gender: studentForm.gender || "Other",
        contact: studentForm.contact ? studentForm.contact.trim() : null,
      };

      if (editingStudent) {
        (await dashboardApi.updateStudent?.(editingStudent.id, payload)) ||
          fetch(`${API_ROOT}/school/students/${editingStudent.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
        setStudents((prev) =>
          prev.map((s) =>
            s.id === editingStudent.id
              ? {
                  ...s,
                  name: payload.name,
                  class: payload.class_name,
                  section: payload.section,
                  age: payload.age,
                  gender: payload.gender,
                }
              : s,
          ),
        );
      } else {
        const userId =
          userDetails?.user_id || localStorage.getItem("neuro_user_id");
        const userIdParam = userId
          ? `?created_by=${encodeURIComponent(userId)}`
          : "";
        const res = await (dashboardApi.createStudent?.(payload) ||
          fetch(`${API_ROOT}/school/students${userIdParam}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }).then((r) => r.json().catch(() => ({}))));
        const newId = res?.data?.student_id || res?.student_id || null;

        if (!newId) {
          console.error("saveStudent: Missing student_id in response", res);
        }

        try {
          const listRes = await authFetch(
            `${API_ROOT}/school/students${userIdParam}`,
            { headers: authHeaders() },
          ).then((r) => r.json().catch(() => ({})));
          const studentsData = extractList(listRes);
          const mappedStudents = (studentsData || []).map(mapStudentRecord);
          setStudents(mappedStudents);
          if (newId) {
            setSelectedEntity({ type: "student", id: String(newId) });
          }
        } catch (refreshErr) {
          console.error("Failed to refresh students after create", refreshErr);
          if (newId) {
            const fallback = mapStudentRecord({
              student_id: String(newId),
              name: payload.name,
              class: payload.class_name,
              section: payload.section,
              age: payload.age,
              gender: payload.gender,
              contact: payload.contact,
              eve: {},
              burnout_score: 0,
            });
            setStudents((prev) => [fallback, ...prev]);
            setSelectedEntity({ type: "student", id: String(newId) });
          }
        }
      }
      setShowStudentModal(false);
      setEditingStudent(null);
      setStudentForm({
        name: "",
        class_name: "",
        section: "",
        age: "",
        gender: "Other",
        contact: "",
      });
    } catch (e) {
      console.error("Save student failed", e);
      alert(t("Failed to save student"));
    } finally {
      setSavingStudent(false);
    }
  }

  async function deleteStudent(id) {
    if (!window.confirm("Delete this student?")) return;
    try {
      (await dashboardApi.deleteStudent?.(id)) ||
        fetch(`${API_ROOT}/school/students/${id}`, { method: "DELETE" });
      setStudents((prev) => prev.filter((s) => s.id !== id));
      if (selectedEntity?.type === "student" && selectedEntity?.id === id)
        setSelectedEntity(null);
    } catch (e) {
      console.error("Delete student failed", e);
      alert(t("Failed to delete student"));
    }
  }

  // Staff CRUD
  async function saveStaff() {
    if (savingStaff) return;
    const name = staffForm.name.trim();
    if (!name) return alert(t("Please enter staff name"));
    setSavingStaff(true);
    try {
      const updatePayload = {
        name: staffForm.name,
        role: staffForm.designation,
        contact: staffForm.contact,
      };
      if (editingStaff) {
        const res = await authFetch(`${API_ROOT}/school/staff/${editingStaff.id}`, {
          method: "PUT",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify(updatePayload),
        });
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(
            errorData.detail || errorData.message || `HTTP ${res.status}`,
          );
        }
        setStaff((prev) =>
          prev.map((s) =>
            s.id === normalizeId(editingStaff.id)
              ? {
                  ...s,
                  name: staffForm.name,
                  role: staffForm.designation,
                  contact: staffForm.contact,
                }
              : s,
          ),
        );
        setShowDetailsModal(false);
      } else {
        // create via school API (school_students table)
        const userId =
          userDetails?.user_id || localStorage.getItem("neuro_user_id");
        const userIdParam = userId
          ? `?created_by=${encodeURIComponent(userId)}`
          : "";
        const payload = {
          name: staffForm.name,
          role: staffForm.designation,
          contact: staffForm.contact,
        };
        const res = await (dashboardApi.createSchoolStaff?.(payload, userId) ||
          fetch(`${API_ROOT}/school/staff${userIdParam}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }).then((r) => r.json().catch(() => ({}))));
        const newId = res?.data?.staff_id || res?.staff_id || null;

        if (!newId) {
          console.error("saveStaff: Missing staff_id in response", res);
        }

        try {
          const listRes = await (dashboardApi.getSchoolStaff?.(userId) ||
            fetch(`${API_ROOT}/school/staff${userIdParam}`).then((r) =>
              r.json().catch(() => ({})),
            ));
          const staffData = extractList(listRes);
          const mappedStaff = (staffData || [])
            .map(mapStaffRecord)
            .filter(Boolean);
          setStaff(mappedStaff);
          if (newId) {
            setSelectedEntity({ type: "staff", id: String(newId) });
          }
        } catch (err) {
          console.error("Failed to refresh staff after create", err);
          if (newId) {
            const fallback = mapStaffRecord({
              staff_id: String(newId),
              name: staffForm.name,
              role: staffForm.designation,
              contact: staffForm.contact,
              eve: {},
              burnout_score: 0,
            });
            setStaff((prev) => (fallback ? [fallback, ...prev] : prev));
            setSelectedEntity({ type: "staff", id: String(newId) });
          }
        }
      }
      setShowStaffModal(false);
      setEditingStaff(null);
      setStaffForm({ name: "", designation: "Psychologist", contact: "" });
    } catch (e) {
      console.error("Save staff failed", e);
      const msg = e?.message
        ? `${t("Failed to save staff")}: ${e.message}`
        : t("Failed to save staff");
      alert(msg);
    } finally {
      setSavingStaff(false);
    }
  }

  async function deleteStaff(id) {
    if (!window.confirm("Delete this staff member?")) return;
    try {
      (await dashboardApi.deleteStaff?.(id)) ||
        fetch(`${API_ROOT}/school/staff/${id}`, { method: "DELETE" });
      setStaff((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      console.error("Delete staff failed", e);
      alert(t("Failed to delete staff"));
    }
  }

  // Remove the "Staff Quick Actions" su-card from the DOM (safe, non-destructive)
  useEffect(() => {
    // run once on mount
    const removeStaffQuickActions = () => {
      document.querySelectorAll(".su-card").forEach((card) => {
        if (
          card.textContent &&
          card.textContent.trim().startsWith("Staff Quick Actions")
        ) {
          card.remove();
        }
      });
    };
    // small timeout to allow initial render
    const t = setTimeout(removeStaffQuickActions, 40);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="su-root">
      <header className="su-topbar">
        <div className="su-brand">
          {displayUserName
            ? `${displayUserName} - ${t("School / University Dashboard")}`
            : t("School / University Dashboard")}
        </div>
        <div className="su-actions">
          {/* <button className="su-btn" onClick={() => window.location.href = '/school/report'}>View Detailed Report</button> */}
          <button
            aria-label={t("Toggle theme")}
            className="su-theme-toggle"
            onClick={toggleTheme}
            title={`${t("Theme")}: ${theme.charAt(0).toUpperCase() + theme.slice(1)}`}
          >
            {theme === "light" && (
              <span role="img" aria-label={t("Light")}>
                🌞
              </span>
            )}
            {theme === "dark" && (
              <span role="img" aria-label={t("Dark")}>
                🌜
              </span>
            )}
            {theme === "device" && (
              <span role="img" aria-label={t("Device")}>
                💻
              </span>
            )}
          </button>
        </div>
      </header>

      <main className="su-main">
        {/* top area now hosts centered Tabs */}

        <div className="su-tabs su-tabs-top">
          <button
            className={`su-tab large ${activeTab === "students" ? "active" : ""}`}
            onClick={() => setActiveTab("students")}
            aria-pressed={activeTab === "students"}
          >
            {t("Students")}
          </button>
          <button
            className={`su-tab large ${activeTab === "staff" ? "active" : ""}`}
            onClick={() => setActiveTab("staff")}
            aria-pressed={activeTab === "staff"}
          >
            {t("Staff")}
          </button>
        </div>

        <section className="su-body-grid">
          <StudentListPanel
            t={t}
            activeTab={activeTab}
            students={students}
            staff={staff}
            selectedEntity={selectedEntity}
            setSelectedEntity={setSelectedEntity}
            onAddStudent={() => {
              setEditingStudent(null);
              setStudentForm({
                name: "",
                class_name: "",
                section: "",
                age: "",
                gender: "Other",
                contact: "",
              });
              setShowStudentModal(true);
            }}
            onEditStudent={(s) => {
              setEditingStudent(s);
              setStudentForm({
                name: s.name,
                class_name: s.class || "",
                section: s.section,
                age: s.age || "",
                gender: s.gender || "Other",
                contact: s.contact || "",
              });
              setShowStudentModal(true);
            }}
            onDeleteStudent={deleteStudent}
            onAddStaff={() => {
              setEditingStaff(null);
              setStaffForm({
                name: "",
                designation: "Psychologist",
                contact: "",
              });
              setShowStaffModal(true);
            }}
            onEditStaff={(st) => {
              setEditingStaff(st);
              setStaffForm({
                name: st.name,
                designation: st.role,
                contact: st.contact,
              });
              setShowStaffModal(true);
            }}
            onDeleteStaff={deleteStaff}
            studentImportProps={studentImportProps}
            staffImportProps={staffImportProps}
          />

          <section className="su-center">
            <div className="su-student-overview card">
              {selectedEntity ? (
                <>
                  <div className="su-overview-head">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "20px",
                        flex: 1,
                      }}
                    >
                      <div style={{ position: "relative", flexShrink: 0 }}>
                        <label
                          htmlFor={`profile-pic-upload-${selectedEntity?.id}`}
                          style={{
                            cursor: uploadingProfilePic ? "wait" : "pointer",
                            display: "inline-block",
                            position: "relative",
                          }}
                          title={t("Click to upload profile picture")}
                        >
                          <img
                            src={
                              selectedRecord?.avatar ||
                              selectedRecord?.profile_picture ||
                              DEFAULT_AVATAR
                            }
                            alt={selectedRecord?.name || "Profile"}
                            style={{
                              width: "120px",
                              height: "120px",
                              borderRadius: "50%",
                              objectFit: "contain",
                              objectPosition: "center",
                              backgroundColor: "#f3f4f6",
                              border: "3px solid #e5e7eb",
                              display: "block",
                              opacity: uploadingProfilePic ? 0.6 : 1,
                              transition: "opacity 0.2s",
                              padding: "4px",
                            }}
                          />
                          {uploadingProfilePic && (
                            <div
                              style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                fontSize: "12px",
                                color: "#6b7280",
                              }}
                            >
                              {t("Uploading...")}
                            </div>
                          )}
                          <div
                            style={{
                              position: "absolute",
                              bottom: "4px",
                              right: "4px",
                              width: "32px",
                              height: "32px",
                              borderRadius: "50%",
                              background: "#3b82f6",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "3px solid white",
                              fontSize: "14px",
                              color: "white",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                          >
                            📷
                          </div>
                        </label>
                        <input
                          id={`profile-pic-upload-${selectedEntity?.id}`}
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file && selectedEntity?.id) {
                              handleProfilePictureUpload(
                                file,
                                selectedEntity.type,
                                selectedEntity.id,
                              );
                            }
                            e.target.value = ""; // Reset input
                          }}
                          disabled={uploadingProfilePic}
                        />
                      </div>
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                      >
                        <h3 style={{ marginBottom: "8px", marginTop: 0 }}>
                          {selectedRecord?.name || "—"}
                        </h3>
                        {selectedEntity?.type === "student" &&
                          selectedRecord?.class && (
                            <div
                              style={{
                                fontSize: "14px",
                                color: "#6B7280",
                                marginBottom: "8px",
                              }}
                            >
                              {t("Class")} {selectedRecord.class}
                              {selectedRecord.section
                                ? ` • ${selectedRecord.section}`
                                : ""}
                            </div>
                          )}
                        <div className="su-small-meta">
                          {t("Last session")}:{" "}
                          {selectedRecord?.last_session || "—"}
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="su-report-toggle">
                        {["weekly", "monthly", "yearly"].map((range) => (
                          <button
                            key={range}
                            type="button"
                            className={`su-report-btn ${showReportModal && reportRange === range ? "active" : ""}`}
                            onClick={() => handleOpenReport(range)}
                          >
                            {t(range.charAt(0).toUpperCase() + range.slice(1))}
                          </button>
                        ))}
                        <button
                          type="button"
                          className={`su-history-btn ${historyState.entityId === selectedEntity.id ? "active" : ""}`}
                          onClick={toggleHistory}
                        >
                          🕓 {t("History")}
                        </button>
                      </div>
                      <button
                        type="button"
                        className="su-details-btn"
                        onClick={() => {
                          if (
                            selectedEntity?.type === "student" &&
                            selectedRecord
                          ) {
                            setEditingStudent(selectedRecord);
                            setStudentForm({
                              name: selectedRecord.name || "",
                              class_name: selectedRecord.class || "",
                              section: selectedRecord.section || "",
                              age: selectedRecord.age || "",
                              gender: selectedRecord.gender || "Other",
                              contact: selectedRecord.contact || "",
                            });
                          } else if (
                            selectedEntity?.type === "staff" &&
                            selectedRecord
                          ) {
                            setEditingStaff(selectedRecord);
                            setStaffForm({
                              name: selectedRecord.name || "",
                              designation:
                                selectedRecord.role || "Psychologist",
                              contact: selectedRecord.contact || "",
                            });
                          }
                          setShowDetailsModal(true);
                        }}
                        style={{
                          marginTop: "12px",
                          padding: "10px 20px",
                          borderRadius: "12px",
                          border: "none",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          fontSize: "14px",
                          fontWeight: "600",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          justifyContent: "center",
                          transition: "all 0.3s ease",
                          boxShadow: "0 4px 6px rgba(102, 126, 234, 0.3)",
                          width: "100%",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow =
                            "0 6px 12px rgba(102, 126, 234, 0.4)";
                          e.target.style.background =
                            "linear-gradient(135deg, #764ba2 0%, #667eea 100%)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow =
                            "0 4px 6px rgba(102, 126, 234, 0.3)";
                          e.target.style.background =
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
                        }}
                      >
                        <span style={{ fontSize: "16px" }}>👤</span>
                        {selectedEntity?.type === "student"
                          ? t("Student Details")
                          : t("Staff Details")}
                      </button>
                      
                      
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {historyState.entityId === selectedEntity.id && (
                      <motion.div
                        className="su-history-panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {historyState.loading && (
                          <div className="su-empty">
                            {t("Loading history…")}
                          </div>
                        )}
                        {!historyState.loading && historyState.error && (
                          <div
                            className="su-empty"
                            style={{ color: "#DC2626" }}
                          >
                            {historyState.error}
                          </div>
                        )}
                        {!historyState.loading &&
                          !historyState.error &&
                          historyState.data.length === 0 && (
                            <div className="su-empty">
                              {t("No history available yet.")}
                            </div>
                          )}
                        {!historyState.loading &&
                          !historyState.error &&
                          historyState.data.length > 0 && (
                            <div className="su-history-table">
                              <div className="su-history-row head">
                                <span>{t("Date")}</span>
                                <span>{t("Calm")}</span>
                                <span>{t("Stressed")}</span>
                                <span>{t("Anxious")}</span>
                                <span>{t("Demotivated")}</span>
                                <span>{t("Hurt")}</span>
                                <span>{t("Notes")}</span>
                                <span>🏆 {t("Top Emotion")}</span>
                              </div>
                              <div className="su-history-body">
                                {historyState.data.map((entry, idx) => {
                                  const calm = toPercentage(entry.calm);
                                  const stressed = toPercentage(entry.stressed);
                                  const anxious = toPercentage(entry.anxious);
                                  const demotivated = toPercentage(
                                    entry.demotivated,
                                  );
                                  const hurt = toPercentage(entry.hurt);
                                  const emotions = [
                                    {
                                      label: t("Calm"),
                                      value: entry.calm,
                                      badge: "calm",
                                    },
                                    {
                                      label: t("Stressed"),
                                      value: entry.stressed,
                                      badge: "stressed",
                                    },
                                    {
                                      label: t("Anxious"),
                                      value: entry.anxious,
                                      badge: "anxious",
                                    },
                                    {
                                      label: t("Demotivated"),
                                      value: entry.demotivated,
                                      badge: "demotivated",
                                    },
                                    {
                                      label: t("Hurt"),
                                      value: entry.hurt,
                                      badge: "hurt",
                                    },
                                  ];
                                  const hasEmotionData = emotions.some(
                                    (e) => Number(e.value ?? 0) > 0,
                                  );
                                  const topEmotion = hasEmotionData
                                    ? emotions.reduce(
                                        (acc, curr) => {
                                          const currVal = Number(
                                            curr.value ?? 0,
                                          );
                                          const accVal = Number(acc.value ?? 0);
                                          return currVal > accVal ? curr : acc;
                                        },
                                        {
                                          label: t("Calm"),
                                          value: entry.calm,
                                          badge: "calm",
                                        },
                                      )
                                    : null;
                                  return (
                                    <div
                                      key={entry.session_id || idx}
                                      className={`su-history-row ${idx % 2 === 0 ? "even" : "odd"}`}
                                    >
                                      <span>
                                        {entry.session_date ||
                                          entry.created_at ||
                                          "—"}
                                      </span>
                                      <span>{calm}</span>
                                      <span>{stressed}</span>
                                      <span>{anxious}</span>
                                      <span>{demotivated}</span>
                                      <span>{hurt}</span>
                                      <span className="su-history-note">
                                        {entry.note_preview
                                          ? truncateText(entry.note_preview, 90)
                                          : "—"}
                                      </span>
                                      <span>
                                        {topEmotion ? (
                                          <span
                                            className={`su-badge su-badge-${topEmotion.badge}`}
                                          >
                                            {topEmotion.label} (
                                            {toPercentage(topEmotion.value)})
                                          </span>
                                        ) : (
                                          "—"
                                        )}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="su-eve-grid">
                    <ProgressBar
                      label={t("Calm")}
                      value={selectedRecord?.eve?.calm || 0}
                      color="#6fcf97"
                    />
                    <ProgressBar
                      label={t("Stressed")}
                      value={selectedRecord?.eve?.stressed || 0}
                      color="#eb5757"
                    />
                    <ProgressBar
                      label={t("Demotivated")}
                      value={selectedRecord?.eve?.demotivated || 0}
                      color="#f2c94c"
                    />
                    <ProgressBar
                      label={t("Anxious")}
                      value={selectedRecord?.eve?.anxious || 0}
                      color="#bb6bd9"
                    />
                    <ProgressBar
                      label={t("Hurt")}
                      value={selectedRecord?.eve?.hurt || 0}
                      color="#56ccf2"
                    />
                  </div>

                  <div className="su-card-row">
                    <div className="su-small-card">
                      <div className="su-small-title">{t("Burnout Score")}</div>
                      <div className="su-bold">
                        {(() => {
                          const sel = selectedEntity
                            ? selectedEntity.type === "student"
                              ? students.find((s) => s.id === selectedEntity.id)
                              : staff.find((s) => s.id === selectedEntity.id)
                            : null;
                          return sel?.burnout_score || 0;
                        })()}
                        %
                      </div>
                      <button
                        className="su-ghost"
                        onClick={() => setShowEmotionBreakdown(true)}
                      >
                        {t("Show Emotion Breakdown")}
                      </button>
                    </div>
                    <div className="su-small-card">
                      <div className="su-small-title">
                        {t("Behavioral Flags")}
                      </div>
                      <div className="su-small-list">—</div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="su-empty-center">
                  {t(
                    "Select a student/staff to start session and view reports",
                  )}
                </div>
              )}
            </div>

            <div className="su-cards-grid">
              {/* Student view */}
              {(!selectedEntity || selectedEntity.type === "student") &&
                userMetrics &&
                userMetrics.type === "student" && (
                  <>
                    <motion.div
                      className="su-analytic-card"
                      whileHover={{ scale: 1.02 }}
                    >
                      <h4>{t("Burnout & Academic Stress Risk")}</h4>
                      <div className="su-analytic-body">
                        <div className="su-risk-bar">
                          <div
                            className="su-risk-fill"
                            style={{
                              width: `${userMetrics.burnout.percentage}%`,
                            }}
                          />
                        </div>
                        <div className="su-analytic-footer">
                          <span>
                            {userMetrics.burnout.level} (
                            {userMetrics.burnout.score}%)
                          </span>
                          <span>{userMetrics.burnout.percentage}% of bar</span>
                        </div>
                      </div>
                    </motion.div>

                    <motion.div
                      className="su-analytic-card"
                      whileHover={{ scale: 1.02 }}
                    >
                      <h4>{t("Behavioral Deviation")}</h4>
                      <div className="su-analytic-body">
                        <div className="su-flag-summary">
                          {userMetrics.behavioral.count}{" "}
                          {t("Flags Raised This Week")} •{" "}
                          {userMetrics.behavioral.flags
                            .map((f) => t(f.type))
                            .join(" • ") || "—"}
                        </div>
                      </div>
                    </motion.div>

                    <motion.div
                      className="su-analytic-card"
                      whileHover={{ scale: 1.02 }}
                    >
                      <h4>{t("Learning Readiness")}</h4>
                      <div className="su-analytic-body">
                        <div className="su-readiness-bars">
                          {(() => {
                            const hasData =
                              selectedRecord &&
                              selectedRecord.eve &&
                              Object.values(selectedRecord.eve || {}).some(
                                (v) => Number(v) > 0,
                              );
                            if (!hasData) {
                              return (
                                <>
                                  <div className="su-readiness-row">
                                    <span>{t("Motivated")}</span>
                                    <div
                                      className="su-readiness-fill"
                                      style={{ width: "0%" }}
                                    >
                                      <strong>—</strong>
                                    </div>
                                  </div>
                                  <div className="su-readiness-row">
                                    <span>{t("Partially Ready")}</span>
                                    <div
                                      className="su-readiness-fill"
                                      style={{ width: "0%" }}
                                    >
                                      <strong>—</strong>
                                    </div>
                                  </div>
                                  <div className="su-readiness-row">
                                    <span>{t("Disengaged")}</span>
                                    <div
                                      className="su-readiness-fill"
                                      style={{ width: "0%" }}
                                    >
                                      <strong>—</strong>
                                    </div>
                                  </div>
                                </>
                              );
                            }
                            return (
                              <>
                                <div className="su-readiness-row">
                                  <span>{t("Motivated")}</span>
                                  <div
                                    className="su-readiness-fill"
                                    style={{
                                      width: `${userMetrics.readiness.motivated}%`,
                                    }}
                                  >
                                    <strong>
                                      {userMetrics.readiness.motivated}%
                                    </strong>
                                  </div>
                                </div>
                                <div className="su-readiness-row">
                                  <span>{t("Partially Ready")}</span>
                                  <div
                                    className="su-readiness-fill"
                                    style={{
                                      width: `${userMetrics.readiness.partiallyReady}%`,
                                    }}
                                  >
                                    <strong>
                                      {userMetrics.readiness.partiallyReady}%
                                    </strong>
                                  </div>
                                </div>
                                <div className="su-readiness-row">
                                  <span>{t("Disengaged")}</span>
                                  <div
                                    className="su-readiness-fill"
                                    style={{
                                      width: `${userMetrics.readiness.disengaged}%`,
                                    }}
                                  >
                                    <strong>
                                      {userMetrics.readiness.disengaged}%
                                    </strong>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </motion.div>
                  </>
                )}

              {/* Staff view */}
              {selectedEntity &&
                selectedEntity.type === "staff" &&
                userMetrics &&
                userMetrics.type === "staff" && (
                  <>
                    <motion.div
                      className="su-analytic-card"
                      whileHover={{ scale: 1.02 }}
                    >
                      <h4>{t("Professional Burnout Score")}</h4>
                      <div className="su-analytic-body">
                        <div className="su-risk-bar">
                          <div
                            className="su-risk-fill"
                            style={{
                              width: `${userMetrics.professionalBurnout.percentage}%`,
                            }}
                          />
                        </div>
                        <div className="su-analytic-footer">
                          <span>
                            {userMetrics.professionalBurnout.level} (
                            {userMetrics.professionalBurnout.score}%)
                          </span>
                          <span>
                            {userMetrics.professionalBurnout.percentage}%
                          </span>
                        </div>
                      </div>
                    </motion.div>

                    <motion.div
                      className="su-analytic-card"
                      whileHover={{ scale: 1.02 }}
                    >
                      <h4>{t("Emotional Well-being Indicators")}</h4>
                      <div className="su-analytic-body">
                        <div className="su-flag-summary">
                          {userMetrics.emotionalIndicators.count}{" "}
                          {t("Concerns Identified")} •{" "}
                          {userMetrics.emotionalIndicators.indicators
                            .map((i) => t(i.type))
                            .join(" • ") || "—"}
                        </div>
                        <div className="su-flag-chips" style={{ marginTop: 8 }}>
                          {userMetrics.emotionalIndicators.indicators.map(
                            (f, i) => (
                              <span
                                key={i}
                                className={`su-flag-chip severity-${f.severity}`}
                              >
                                {t(f.type)}
                              </span>
                            ),
                          )}
                        </div>
                      </div>
                    </motion.div>

                    <motion.div
                      className="su-analytic-card"
                      whileHover={{ scale: 1.02 }}
                    >
                      <h4>{t("Work Engagement & Resilience")}</h4>
                      <div className="su-analytic-body">
                        <div className="su-readiness-bars">
                          {(() => {
                            const hasData =
                              selectedRecord &&
                              selectedRecord.eve &&
                              Object.values(selectedRecord.eve || {}).some(
                                (v) => Number(v) > 0,
                              );
                            if (!hasData) {
                              return (
                                <>
                                  <div className="su-readiness-row">
                                    <span>{t("High Engagement")}</span>
                                    <div
                                      className="su-readiness-fill"
                                      style={{
                                        width: "0%",
                                        background:
                                          "linear-gradient(90deg,#5eead4,#06b6d4)",
                                      }}
                                    >
                                      <strong>—</strong>
                                    </div>
                                  </div>
                                  <div className="su-readiness-row">
                                    <span>{t("Moderate")}</span>
                                    <div
                                      className="su-readiness-fill"
                                      style={{
                                        width: "0%",
                                        background:
                                          "linear-gradient(90deg,#fbbf24,#f97316)",
                                      }}
                                    >
                                      <strong>—</strong>
                                    </div>
                                  </div>
                                  <div className="su-readiness-row">
                                    <span>{t("At Risk")}</span>
                                    <div
                                      className="su-readiness-fill"
                                      style={{
                                        width: "0%",
                                        background:
                                          "linear-gradient(90deg,#ef4444,#b91c1c)",
                                      }}
                                    >
                                      <strong>—</strong>
                                    </div>
                                  </div>
                                </>
                              );
                            }
                            return (
                              <>
                                <div className="su-readiness-row">
                                  <span>{t("High Engagement")}</span>
                                  <div
                                    className="su-readiness-fill"
                                    style={{
                                      width: `${userMetrics.workEngagement.highEngagement}%`,
                                      background:
                                        "linear-gradient(90deg,#5eead4,#06b6d4)",
                                    }}
                                  >
                                    <strong>
                                      {
                                        userMetrics.workEngagement
                                          .highEngagement
                                      }
                                      %
                                    </strong>
                                  </div>
                                </div>
                                <div className="su-readiness-row">
                                  <span>{t("Moderate")}</span>
                                  <div
                                    className="su-readiness-fill"
                                    style={{
                                      width: `${userMetrics.workEngagement.moderate}%`,
                                      background:
                                        "linear-gradient(90deg,#fbbf24,#f97316)",
                                    }}
                                  >
                                    <strong>
                                      {userMetrics.workEngagement.moderate}%
                                    </strong>
                                  </div>
                                </div>
                                <div className="su-readiness-row">
                                  <span>{t("At Risk")}</span>
                                  <div
                                    className="su-readiness-fill"
                                    style={{
                                      width: `${userMetrics.workEngagement.atRisk}%`,
                                      background:
                                        "linear-gradient(90deg,#ef4444,#b91c1c)",
                                    }}
                                  >
                                    <strong>
                                      {userMetrics.workEngagement.atRisk}%
                                    </strong>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </motion.div>
                  </>
                )}
            </div>

            {/* recent uploads / small charts */}
            <div className="su-recent-section">
              <h4>{t("Recent Uploads")}</h4>
              <div className="su-table">
                <div className="su-tr su-head">
                  <div>{t("Name")}</div>
                  <div>
                    {t("Class")} / {t("Role")}
                  </div>
                  <div>{t("Date")}</div>
                  <div>{t("Top Emotion")}</div>
                </div>
                {(() => {
                  const studentsWithSessions = students
                    .filter(
                      (s) =>
                        s.last_session &&
                        s.eve &&
                        Object.values(s.eve || {}).some((v) => Number(v) > 0),
                    )
                    .map((s) => ({ ...s, type: "student" }));

                  const staffWithSessions = staff
                    .filter(
                      (s) =>
                        s.last_session &&
                        s.eve &&
                        Object.values(s.eve || {}).some((v) => Number(v) > 0),
                    )
                    .map((s) => ({ ...s, type: "staff" }));

                  const allWithSessions = [
                    ...studentsWithSessions,
                    ...staffWithSessions,
                  ]
                    .sort((a, b) => {
                      const dateA = a.last_session
                        ? new Date(a.last_session)
                        : new Date(0);
                      const dateB = b.last_session
                        ? new Date(b.last_session)
                        : new Date(0);
                      return dateB - dateA; // Most recent first
                    })
                    .slice(0, 6);

                  if (allWithSessions.length === 0) {
                    return (
                      <div
                        className="su-tr"
                        style={{
                          textAlign: "center",
                          padding: "1rem",
                          color: "#6b7280",
                        }}
                      >
                        <div style={{ gridColumn: "1 / -1" }}>
                          {t("No recent uploads")}
                        </div>
                      </div>
                    );
                  }

                  return allWithSessions.map((item) => {
                    const topEmotion =
                      Object.entries(item.eve || {}).sort(
                        (a, b) => b[1] - a[1],
                      )[0]?.[0] || "—";
                    return (
                      <div key={`${item.type}-${item.id}`} className="su-tr">
                        <div>{item.name}</div>
                        <div>
                          {item.type === "student"
                            ? item.class || "—"
                            : item.role || "—"}
                        </div>
                        <div>{item.last_session || "—"}</div>
                        <div>{topEmotion}</div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </section>

          <SessionRecordingPanel
            t={t}
            selectedEntity={selectedEntity}
            timer={timer}
            running={running}
            segmentRecording={segmentRecording}
            continuousRecording={continuousRecording}
            micClicks={micClicks}
            handleMicClick={handleMicClick}
            startContinuousRecording={startContinuousRecording}
            lastDataSource={lastDataSource}
            lastLatencyMs={lastLatencyMs}
            liveNoteText={liveNoteText}
            setLiveNoteText={setLiveNoteText}
            noteRecState={noteRecState}
            noteError={noteError}
            startNoteRecording={startNoteRecording}
            stopNoteRecording={stopNoteRecording}
            saveNote={saveNote}
            notesByEntity={notesByEntity}
            currentSessionId={currentSessionId}
            deleteNote={deleteNote}
            DEFAULT_AVATAR={DEFAULT_AVATAR}
          />
        </section>
      </main>

      {/* Student Modal */}
      {showStudentModal && (
        <motion.div
          className="su-modal-bg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={(e) => {
            if (e.target.classList.contains("su-modal-bg"))
              setShowStudentModal(false);
          }}
        >
          <motion.div
            className="su-modal"
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>{editingStudent ? t("Edit Student") : t("Add Student")}</h3>
            <div className="su-form">
              <label>
                {t("Name")}
                <input
                  value={studentForm.name}
                  onChange={(e) =>
                    setStudentForm((f) => ({ ...f, name: e.target.value }))
                  }
                />
              </label>
              <label>
                {t("Class")}
                <input
                  value={studentForm.class_name}
                  onChange={(e) =>
                    setStudentForm((f) => ({
                      ...f,
                      class_name: e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                {t("Section")}
                <input
                  value={studentForm.section}
                  onChange={(e) =>
                    setStudentForm((f) => ({ ...f, section: e.target.value }))
                  }
                />
              </label>
              <label>
                {t("Age")}
                <input
                  value={studentForm.age}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onChange={(e) =>
                    setStudentForm((f) => ({
                      ...f,
                      age: e.target.value.replace(/[^0-9]/g, ""),
                    }))
                  }
                />
              </label>
              <label>
                {t("Contact")}
                <input
                  value={studentForm.contact}
                  inputMode="tel"
                  onChange={(e) =>
                    setStudentForm((f) => ({
                      ...f,
                      contact: e.target.value.replace(/[^0-9+\-\s]/g, ""),
                    }))
                  }
                />
              </label>
              <label>
                {t("Gender")}
                <select
                  value={studentForm.gender}
                  onChange={(e) =>
                    setStudentForm((f) => ({ ...f, gender: e.target.value }))
                  }
                >
                  <option>{t("Male")}</option>
                  <option>{t("Female")}</option>
                  <option>{t("Other")}</option>
                </select>
              </label>
            </div>
            <div className="su-modal-actions">
              <button
                className="su-secondary"
                onClick={() => setShowStudentModal(false)}
                disabled={savingStudent}
              >
                {t("Cancel")}
              </button>
              <button
                className="su-primary"
                onClick={saveStudent}
                disabled={savingStudent}
              >
                {savingStudent ? t("Saving...") : t("Save")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Staff Modal */}
      {showStaffModal && (
        <motion.div
          className="su-modal-bg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={(e) => {
            if (e.target.classList.contains("su-modal-bg"))
              setShowStaffModal(false);
          }}
        >
          <motion.div
            className="su-modal"
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>{editingStaff ? t("Edit Staff") : t("Add Staff")}</h3>
            <div className="su-form">
              <label>
                {t("Name")}
                <input
                  value={staffForm.name}
                  onChange={(e) =>
                    setStaffForm((f) => ({ ...f, name: e.target.value }))
                  }
                />
              </label>
              <label>
                {t("Role")}
                <input
                  value={staffForm.designation}
                  onChange={(e) =>
                    setStaffForm((f) => ({ ...f, designation: e.target.value }))
                  }
                />
              </label>
              <label>
                {t("Contact")}
                <input
                  value={staffForm.contact}
                  inputMode="tel"
                  onChange={(e) =>
                    setStaffForm((f) => ({
                      ...f,
                      contact: e.target.value.replace(/[^0-9+\-\s]/g, ""),
                    }))
                  }
                />
              </label>
            </div>
            <div className="su-modal-actions">
              <button
                className="su-secondary"
                onClick={() => setShowStaffModal(false)}
                disabled={savingStaff}
              >
                {t("Cancel")}
              </button>
              <button
                className="su-primary"
                onClick={saveStaff}
                disabled={savingStaff}
              >
                {savingStaff ? t("Saving...") : t("Save")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRecord && (
        <motion.div
          className="su-modal-bg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={(e) => {
            if (e.target.classList.contains("su-modal-bg"))
              setShowDetailsModal(false);
          }}
        >
          <motion.div
            className="su-modal"
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "600px", width: "90%" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3>
                {selectedEntity?.type === "student"
                  ? t("Student Details")
                  : t("Staff Details")}
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                  padding: "0",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: "24px", textAlign: "center" }}>
              <img
                src={
                  selectedRecord?.avatar ||
                  selectedRecord?.profile_picture ||
                  DEFAULT_AVATAR
                }
                alt={selectedRecord?.name || "Profile"}
                style={{
                  width: "120px",
                  height: "120px",
                  borderRadius: "50%",
                  objectFit: "contain",
                  objectPosition: "center",
                  backgroundColor: "#f3f4f6",
                  border: "3px solid #e5e7eb",
                  padding: "4px",
                  marginBottom: "16px",
                }}
              />
            </div>

            <div
              style={{
                background: "#f9fafb",
                padding: "24px",
                borderRadius: "12px",
                marginBottom: "20px",
                display: "grid",
                gridTemplateColumns: "140px 1fr",
                gap: "16px 20px",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  color: "#6b7280",
                  fontSize: "13px",
                  textTransform: "uppercase",
                  fontWeight: "600",
                  letterSpacing: "0.5px",
                }}
              >
                {t("Name")}
              </div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#1e3b8b",
                  textAlign: "left",
                }}
              >
                {selectedRecord?.name || "—"}
              </div>

              {selectedEntity?.type === "student" ? (
                <>
                  {selectedRecord?.class && (
                    <>
                      <div
                        style={{
                          color: "#6b7280",
                          fontSize: "13px",
                          textTransform: "uppercase",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("Class")}
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#1e3b8b",
                          textAlign: "left",
                        }}
                      >
                        {selectedRecord.class}
                        {selectedRecord.section
                          ? ` • ${selectedRecord.section}`
                          : ""}
                      </div>
                    </>
                  )}
                  {selectedRecord?.age && (
                    <>
                      <div
                        style={{
                          color: "#6b7280",
                          fontSize: "13px",
                          textTransform: "uppercase",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("Age")}
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#1e3b8b",
                          textAlign: "left",
                        }}
                      >
                        {selectedRecord.age}
                      </div>
                    </>
                  )}
                  {selectedRecord?.gender && (
                    <>
                      <div
                        style={{
                          color: "#6b7280",
                          fontSize: "13px",
                          textTransform: "uppercase",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("Gender")}
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#1e3b8b",
                          textAlign: "left",
                        }}
                      >
                        {selectedRecord.gender}
                      </div>
                    </>
                  )}
                </>
              ) : (
                selectedRecord?.role && (
                  <>
                    <div
                      style={{
                        color: "#6b7280",
                        fontSize: "13px",
                        textTransform: "uppercase",
                        fontWeight: "600",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {t("Role")}
                    </div>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#1e3b8b",
                        textAlign: "left",
                      }}
                    >
                      {selectedRecord.role}
                    </div>
                  </>
                )
              )}

              {selectedRecord?.contact && (
                <>
                  <div
                    style={{
                      color: "#6b7280",
                      fontSize: "13px",
                      textTransform: "uppercase",
                      fontWeight: "600",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {t("Contact")}
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#1e3b8b",
                      textAlign: "left",
                    }}
                  >
                    {selectedRecord.contact}
                  </div>
                </>
              )}

              {selectedRecord?.last_session && (
                <>
                  <div
                    style={{
                      color: "#6b7280",
                      fontSize: "13px",
                      textTransform: "uppercase",
                      fontWeight: "600",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {t("Last session")}
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#1e3b8b",
                      textAlign: "left",
                    }}
                  >
                    {selectedRecord.last_session}
                  </div>
                </>
              )}
            </div>

            <div
              className="su-modal-actions"
              style={{ display: "flex", gap: "12px" }}
            >
              <button
                className="su-secondary"
                onClick={() => setShowDetailsModal(false)}
                style={{ flex: 1 }}
              >
                {t("Close")}
              </button>
              <button
                className="su-primary"
                onClick={() => {
                  setShowDetailsModal(false);
                  if (selectedEntity?.type === "student") {
                    setShowStudentModal(true);
                  } else {
                    setShowStaffModal(true);
                  }
                }}
                style={{
                  flex: 1,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                }}
              >
                ✏️ {t("Edit")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Emotion Breakdown Modal */}
      {showEmotionBreakdown && selectedRecord && (
        <motion.div
          className="su-modal-bg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={(e) => {
            if (e.target.classList.contains("su-modal-bg"))
              setShowEmotionBreakdown(false);
          }}
        >
          <motion.div
            className="su-modal"
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "500px", width: "90%" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3>{t("Emotion Breakdown")}</h3>
              <button
                onClick={() => setShowEmotionBreakdown(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
              }}
            >
              {[
                {
                  label: "Calm",
                  value: selectedRecord?.eve?.calm,
                  color: "#22c55e",
                },
                {
                  label: "Stressed",
                  value: selectedRecord?.eve?.stressed,
                  color: "#ef4444",
                },
                {
                  label: "Demotivated",
                  value: selectedRecord?.eve?.demotivated,
                  color: "#f59e0b",
                },
                {
                  label: "Anxious",
                  value: selectedRecord?.eve?.anxious,
                  color: "#8b5cf6",
                },
                {
                  label: "Hurt",
                  value: selectedRecord?.eve?.hurt,
                  color: "#06b6d4",
                },
                {
                  label: "Burnout Score",
                  value: selectedRecord?.burnout_score,
                  color: "#ec4899",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    background: "rgba(59, 130, 246, 0.08)",
                    borderRadius: "12px",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      marginBottom: "8px",
                    }}
                  >
                    {t(item.label)}
                  </div>
                  <div
                    style={{
                      fontSize: "24px",
                      fontWeight: "700",
                      color: item.color,
                    }}
                  >
                    {item.value !== undefined && item.value !== null
                      ? `${Math.round(Number(item.value) * 100)}%`
                      : "—"}
                  </div>
                </div>
              ))}
            </div>
            <div className="su-modal-actions">
              <button
                className="su-primary"
                onClick={() => setShowEmotionBreakdown(false)}
                style={{ width: "100%" }}
              >
                {t("Close")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      <AnimatePresence>
        {showReportModal && (
          <ReportModal
            key={`${selectedEntity?.id || "none"}-${reportRange || "none"}`}
            visible={showReportModal}
            onClose={handleCloseReport}
            entityType={selectedEntity?.type}
            range={reportRange}
            data={reportData}
            loading={reportLoading}
            error={reportError}
            t={t}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function toPercentage(value) {
  if (value === null || value === undefined) return "0%";
  const num = Number(value);
  if (Number.isNaN(num)) return "0%";
  const normalized = num <= 1 ? num * 100 : num;
  return `${Math.round(normalized)}%`;
}

function truncateText(text, maxLength = 90) {
  if (!text) return "";
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength - 1)}…`;
}

async function enrichHistoryWithNotes(entries) {
  return Promise.all(
    entries.map(async (entry) => {
      const sessionId = entry.session_id || entry.sessionId || entry.id;
      if (!sessionId) return { ...entry, note_preview: "" };
      try {
        const res = await authFetch(
          `${API_ROOT}/school/session/${sessionId}/notes`,
          { headers: authHeaders() },
        ).then((r) => r.json().catch(() => ({})));
        const data = res?.data || res;
        if (Array.isArray(data) && data.length > 0) {
          const latest = data[0];
          const text = latest?.note_text || latest?.text || "";
          return { ...entry, note_preview: text };
        }
        return { ...entry, note_preview: "" };
      } catch {
        return { ...entry, note_preview: "" };
      }
    }),
  );
}
